import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetAICoachGoalsResponse } from 'api/AICoachGoalsApi';
import { AICoachGoalItem } from 'api/LoanOfferApi';
import { AICoachGoalPriority } from 'enums/AICoach';
import { getAICoachGoals, createAICoachGoals, updateAICoachGoals, getGoalProgress } from 'thunks';

export interface YourGoalData {
  loanGoal?: string;
  aiCoachGoals?: AICoachGoalItem[];
  isLoading?: boolean;
  error?: unknown;
  plaidConnected?: boolean;
}

const initialState: YourGoalData = {
  isLoading: false,
};

export enum LoanGoal {
  GetOutDebtFaster = 'get_out_of_debt_faster',
  ConsolidateDebt = 'consolidate_debt',
  LowerMonthlyPayment = 'lower_monthly_payment',
  LowerInterestRate = 'lower_interest_rate',
  FundPersonalExpense = 'fund_personal_expense',
}

const DEBT_CONSOLIDATION_GOALS = [
  LoanGoal.ConsolidateDebt,
  LoanGoal.GetOutDebtFaster,
  LoanGoal.LowerInterestRate,
  LoanGoal.LowerMonthlyPayment,
];

export const goalIsPayOffDebt = (loanGoals?: LoanGoal[]) =>
  !!loanGoals && loanGoals.some((goal) => DEBT_CONSOLIDATION_GOALS.includes(goal));

export const goalIsPersonalFundsOnly = (loanGoals?: LoanGoal[]) =>
  !!loanGoals && loanGoals.includes(LoanGoal.FundPersonalExpense) && loanGoals.length === 1;

const yourGoal = createSlice({
  name: 'yourGoal',
  initialState,
  reducers: {
    setYourGoalData: (state: YourGoalData, { payload }: PayloadAction<YourGoalData>) => {
      state.loanGoal = payload.loanGoal;
      analytics.identify({ loanGoal: payload.loanGoal });
    },
    setCustomGoals: (state: YourGoalData, { payload }: PayloadAction<AICoachGoalItem[]>) => {
      state.aiCoachGoals = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAICoachGoals.pending, (state) => {
      state.isLoading = true;
      state.error = undefined;
    });
    builder.addCase(getAICoachGoals.fulfilled, (state, { payload }: PayloadAction<GetAICoachGoalsResponse>) => {
      state.isLoading = false;
      state.aiCoachGoals = payload.goals;

      if (payload.plaidConnected) {
        state.plaidConnected = payload.plaidConnected;
      }
    });
    builder.addCase(getAICoachGoals.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
    builder.addCase(createAICoachGoals.pending, (state) => {
      state.isLoading = true;
      state.error = undefined;
    });
    builder.addCase(createAICoachGoals.fulfilled, (state, { payload }: PayloadAction<GetAICoachGoalsResponse>) => {
      state.aiCoachGoals = payload.goals;
      state.isLoading = false;
    });
    builder.addCase(createAICoachGoals.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
    builder.addCase(updateAICoachGoals.pending, (state) => {
      state.isLoading = true;
      state.error = undefined;
    });
    builder.addCase(updateAICoachGoals.fulfilled, (state, { payload }: PayloadAction<GetAICoachGoalsResponse>) => {
      state.aiCoachGoals = payload.goals;
      state.isLoading = false;
    });
    builder.addCase(updateAICoachGoals.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
    builder.addCase(getGoalProgress.fulfilled, (state, { payload }) => {
      state.aiCoachGoals = state.aiCoachGoals?.map((goal) => {
        if (goal.priority === AICoachGoalPriority.Primary) {
          return {
            ...goal,
            isCompleted: payload?.isCompleted,
          };
        }
        return goal;
      });
    });
  },
});

export const { setYourGoalData, setCustomGoals } = yourGoal.actions;

export default yourGoal.reducer;
